import { inject } from 'vue';
import type { AxiosInstance } from 'axios';

/**
 * Apps must provide an axios instance using this key (e.g. in main.ts).
 * Apps that use the satellite AxiosPlugin pattern should provide the
 * plugin-initialized instance (the one configured by AxiosPlugin.init()),
 * not a raw axios instance, so interceptors, baseURL, and headers are applied.
 *
 * @example
 * Using AxiosPlugin (recommended): initialize the plugin in main, then provide
 * the configured instance.
 * ```ts
 * import AxiosPlugin from '@satellite/plugins/AxiosPlugin';
 * import { HTTP_CLIENT_KEY } from '@satellite/composables/useHttpClient';
 *
 * // In main.ts: init first so globalThis.axios is the configured client
 * AxiosPlugin.init(redirect401, uiAppName, app);
 * app.provide(HTTP_CLIENT_KEY, globalThis.axios);
 * ```
 *
 * @example
 * If not using AxiosPlugin, provide your own configured axios instance:
 * ```ts
 * import { HTTP_CLIENT_KEY } from '@satellite/composables/useHttpClient';
 * import axios from 'axios';
 *
 * const client = axios.create({ baseURL: '...' });
 * app.provide(HTTP_CLIENT_KEY, client);
 * ```
 */
export const HTTP_CLIENT_KEY = Symbol('httpClient');

/**
 * Returns the HTTP client (axios instance) provided by the app.
 * Use this in satellite code instead of window.axios or a raw axios import.
 *
 * @throws Error if HTTP client was not provided by the app
 * @returns The injected axios instance (plugin-initialized when using AxiosPlugin)
 *
 * @example
 * ```ts
 * const httpClient = useHttpClient();
 * const response = await httpClient.get('/api/custom-forms');
 * ```
 */
export function useHttpClient(): AxiosInstance {
  const client = inject<AxiosInstance>(HTTP_CLIENT_KEY);

  if (!client) {
    throw new Error('[Satellite] HTTP client not provided. Apps must provide an axios instance');
  }

  return client;
}
